const { formatNumberWithCommas, currencyExchange } = window.BAO.Utils

class ExternalKlevuPriceFilter extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  constructor () {
    super()

    const source = window.theme.category || window.theme.searchResults
    this.state = {
      root: source ? source.stateLayer.state : undefined,
      facets: [],
    }

    this.eventBus = source ? source.eventBus : undefined

    if (!this.eventBus) {
      console.error('Event bus not available for FacetsTablet')
    }
    if (this.state.root === undefined) {
      console.error('State root not available for FacetsTablet')
    }

    this._FACET_KEYS = Object.freeze({
      PRICE: 'klevu_price',
      TYPE: 'type',
      GENDER: 'gender',
      BRAND: 'brand',
      TAGS: 'tags',
      CONTACT_US: 'contact_us',
      BACK_IN_STOCK: 'back_in_stock',
      COMING_SOON: 'coming_soon',
    })
  }

  connectedCallback () {
    super.connectedCallback()

    this.subscribeToEvents()
  }

  /**
   * Subscribes to relevant events, primarily for facet data updates.
   */
  subscribeToEvents () {
    if (!this.eventBus) return

    let hasRendered = false

    this.eventBus.subscribe('api:facets:fetched', response => {
      this._processFetchedFacets(response)

      if (!hasRendered) {
        this._renderInitialFacets()
        hasRendered = true
      }
    })
  }

  /**
   * Processes the facet data received from the API.
   * @private
   * @param {Array<Object>} response - The array of facet objects from the API.
   */
  _processFetchedFacets (response) {
    this.state.facets = Array.isArray(response) ? response : []
  }

  /**
   * Renders the initial set of facets into the DOM.
   * This method is typically called once after the first batch of facet data is fetched.
   * @private
   */
  _renderInitialFacets () {
    this.state.facets.forEach((facet, index) => {
      if (facet.key === this._FACET_KEYS.PRICE) {
        const priceGiftContainerHtml = this.buildExternalPriceHTML(facet, index)
        this.insertAdjacentHTML('beforeend', priceGiftContainerHtml)
      }
    })
  }

  buildExternalPriceHTML (facet, index) {
    const existingFacetIndex = this.state.root.activeFacets.findIndex(
      activeFacet => activeFacet.key === 'klevu_price',
    )

    const currentMinFacet = currencyExchange(
      this.state.root.activeFacets[existingFacetIndex]?.values[0],
      false,
    )
    const currentMaxFacet = currencyExchange(
      this.state.root.activeFacets[existingFacetIndex]?.values[1],
      true,
    )

    const minValueInt = facet.min

    let maxValueInt = facet.max
    let maxValue = currencyExchange(maxValueInt, true)
    let suffix = ''
    const actualMaxValue = facet.max

    if (maxValue > parseInt(this.dataset.maxPrice)) {
      maxValueInt = parseInt(this.dataset.maxPrice)
      maxValue = maxValueInt
    }

    const minValue = currencyExchange(minValueInt, false)

    const currentMinValue =
      currentMinFacet && Number(currentMinFacet) >= Number(minValue)
        ? currentMinFacet
        : minValue

    const currentMaxValue =
      currentMaxFacet && Number(currentMaxFacet) <= Number(maxValueInt)
        ? currentMaxFacet
        : maxValue

    let currentMaxInputValue = maxValue

    if (maxValue >= parseInt(this.dataset.maxPrice)) {
      suffix = '+'
      currentMaxInputValue = ''
    }

    const currencySymbol = window.theme.moneyFormat.split('{')[0]
    return `
      <div data-filter-facet-search-external-el="body" data-range-slider-external-el="container">
        <price-range class="prc-Range prc-Range-external">
          <div class="prc-Range_Column">
            <div class="prc-Range_Field">
              <input class="prc-Range_Input" id="price-range-min" type="text" value="${formatNumberWithCommas(
                currentMinValue,
              )}" data-range-slider-external-el="min" placeholder="${currencySymbol}${formatNumberWithCommas(
      currentMinValue,
    )}" data-range-min="${minValue}" data-range-max="${maxValue}" data-price-range-el="min" />

              <label class="prc-Range_Label" for="price-range-min" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${formatNumberWithCommas(
      currentMinValue,
    )}">
                <span class="util-ScreenReaderOnly">
                  {{ 'sections.collection_template.from' | t }}:
                  {{ cart.currency.symbol }}${formatNumberWithCommas(
                    currentMinValue,
                  )}</span>
              </label>
            </div>
          </div>

          <div class="prc-Range_Column prc-Range_Column-range">
            <svg width="25" height="30" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="path-1-inside-1_624_17893" fill="white">
                <rect y="9" width="32" height="8" rx="0.5"/>
              </mask>
                <rect y="9" width="32" height="8" rx="0.5" stroke="currentColor" stroke-width="2" mask="url(#path-1-inside-1_624_17893)"/>
              <mask id="path-2-inside-2_624_17893" fill="white">
                <rect x="2" y="16" width="28" height="20" rx="0.5"/>
              </mask>
              <rect x="2" y="16" width="28" height="20" rx="0.5" stroke="currentColor" stroke-width="2" mask="url(#path-2-inside-2_624_17893)"/>
              <path d="M16 9L16 36" stroke="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.0952 1.45998C9.21985 1.34933 8.3113 1.67073 7.49154 2.88098L7.49155 2.88099C6.67045 4.09831 6.71093 5.06725 7.14301 5.84416C7.60257 6.67048 8.55735 7.37238 9.74004 7.87933C10.9119 8.38163 12.2354 8.66101 13.336 8.69215C13.8867 8.70773 14.361 8.6605 14.7232 8.56057C15.074 8.46377 15.261 8.33444 15.3459 8.22417L15.3299 8.21334C15.4076 8.09885 15.4671 7.87154 15.4237 7.48619C15.3815 7.11216 15.2489 6.65377 15.0298 6.14798C14.592 5.13721 13.8417 4.01218 12.9416 3.11144C12.033 2.2022 11.0294 1.57807 10.0952 1.45998ZM6.65637 2.33085L6.6564 2.33087C5.6632 3.80873 5.62866 5.17869 6.26908 6.3302C6.88333 7.43467 8.07169 8.2522 9.34606 8.79845C10.6313 9.34936 12.0787 9.65697 13.3077 9.69175C13.9217 9.70912 14.5018 9.65904 14.9892 9.52454C15.3507 9.42477 15.701 9.26725 15.9664 9.02186C16.2318 9.26725 16.5821 9.42477 16.9437 9.52454C17.4311 9.65904 18.0111 9.70912 18.6252 9.69175C19.8541 9.65697 21.3015 9.34936 22.5868 8.79845C23.8612 8.2522 25.0495 7.43467 25.6638 6.3302C26.3042 5.17869 26.2696 3.80873 25.2764 2.33087L25.2765 2.33085C24.2776 0.849911 23.02 0.302565 21.7122 0.467878C20.4611 0.626034 19.2627 1.42501 18.2839 2.4046C17.2966 3.39268 16.4742 4.62218 15.9854 5.7505C15.979 5.76524 15.9727 5.77997 15.9664 5.79469C15.9601 5.77997 15.9538 5.76524 15.9474 5.7505C15.4587 4.62218 14.6363 3.39268 13.649 2.4046C12.6701 1.42501 11.4717 0.626034 10.2206 0.467878C8.91287 0.302565 7.65528 0.849911 6.65637 2.33085ZM21.8376 1.45998C22.713 1.34933 23.6215 1.67073 24.4413 2.88098L24.4413 2.88099C25.2624 4.09831 25.2219 5.06725 24.7898 5.84416C24.3303 6.67048 23.3755 7.37238 22.1928 7.87933C21.021 8.38163 19.6974 8.66101 18.5969 8.69215C18.0461 8.70773 17.5718 8.6605 17.2097 8.56057C16.8589 8.46377 16.6719 8.33444 16.587 8.22417L16.603 8.21334C16.5252 8.09885 16.4657 7.87154 16.5091 7.48619C16.5513 7.11216 16.6839 6.65377 16.903 6.14798C17.3409 5.13721 18.0912 4.01218 18.9913 3.11144C19.8998 2.2022 20.9035 1.57807 21.8376 1.45998Z" fill="currentColor"/>
            </svg>

            <range-slider
              class="rng-Slider"
              min="${minValue}"
              max="${maxValue}"
              min-value="${currentMinValue}"
              max-value="${currentMaxValue}">
              <div class="rng-Slider_Inner">
                <button
                  class="rng-Slider_Dot rng-Slider_Dot-left"
                  type="button"
                  data-range-slider-el="touchLeft">
                  <span class="rng-Slider_DotInner"></span>
                </button>

                <button
                  class="rng-Slider_Dot rng-Slider_Dot-right"
                  type="button"
                  data-range-slider-el="touchRight"
                  style="right: 0%;">
                  <span class="rng-Slider_DotInner"></span>
                </button>

                <div class="rng-Slider_Line" data-range-slider-el="line">
                  <span class="rng-Slider_LineInner"></span>
                  <span class="rng-Slider_LineHighlighter" data-range-slider-el="highlighter"></span>
                </div>
              </div>
            </range-slider>

            <svg width="25" height="30" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="path-1-inside-1_624_17893" fill="white">
                <rect y="9" width="32" height="8" rx="0.5"/>
              </mask>
                <rect y="9" width="32" height="8" rx="0.5" stroke="currentColor" stroke-width="2" mask="url(#path-1-inside-1_624_17893)"/>
              <mask id="path-2-inside-2_624_17893" fill="white">
                <rect x="2" y="16" width="28" height="20" rx="0.5"/>
              </mask>
              <rect x="2" y="16" width="28" height="20" rx="0.5" stroke="currentColor" stroke-width="2" mask="url(#path-2-inside-2_624_17893)"/>
              <path d="M16 9L16 36" stroke="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.0952 1.45998C9.21985 1.34933 8.3113 1.67073 7.49154 2.88098L7.49155 2.88099C6.67045 4.09831 6.71093 5.06725 7.14301 5.84416C7.60257 6.67048 8.55735 7.37238 9.74004 7.87933C10.9119 8.38163 12.2354 8.66101 13.336 8.69215C13.8867 8.70773 14.361 8.6605 14.7232 8.56057C15.074 8.46377 15.261 8.33444 15.3459 8.22417L15.3299 8.21334C15.4076 8.09885 15.4671 7.87154 15.4237 7.48619C15.3815 7.11216 15.2489 6.65377 15.0298 6.14798C14.592 5.13721 13.8417 4.01218 12.9416 3.11144C12.033 2.2022 11.0294 1.57807 10.0952 1.45998ZM6.65637 2.33085L6.6564 2.33087C5.6632 3.80873 5.62866 5.17869 6.26908 6.3302C6.88333 7.43467 8.07169 8.2522 9.34606 8.79845C10.6313 9.34936 12.0787 9.65697 13.3077 9.69175C13.9217 9.70912 14.5018 9.65904 14.9892 9.52454C15.3507 9.42477 15.701 9.26725 15.9664 9.02186C16.2318 9.26725 16.5821 9.42477 16.9437 9.52454C17.4311 9.65904 18.0111 9.70912 18.6252 9.69175C19.8541 9.65697 21.3015 9.34936 22.5868 8.79845C23.8612 8.2522 25.0495 7.43467 25.6638 6.3302C26.3042 5.17869 26.2696 3.80873 25.2764 2.33087L25.2765 2.33085C24.2776 0.849911 23.02 0.302565 21.7122 0.467878C20.4611 0.626034 19.2627 1.42501 18.2839 2.4046C17.2966 3.39268 16.4742 4.62218 15.9854 5.7505C15.979 5.76524 15.9727 5.77997 15.9664 5.79469C15.9601 5.77997 15.9538 5.76524 15.9474 5.7505C15.4587 4.62218 14.6363 3.39268 13.649 2.4046C12.6701 1.42501 11.4717 0.626034 10.2206 0.467878C8.91287 0.302565 7.65528 0.849911 6.65637 2.33085ZM21.8376 1.45998C22.713 1.34933 23.6215 1.67073 24.4413 2.88098L24.4413 2.88099C25.2624 4.09831 25.2219 5.06725 24.7898 5.84416C24.3303 6.67048 23.3755 7.37238 22.1928 7.87933C21.021 8.38163 19.6974 8.66101 18.5969 8.69215C18.0461 8.70773 17.5718 8.6605 17.2097 8.56057C16.8589 8.46377 16.6719 8.33444 16.587 8.22417L16.603 8.21334C16.5252 8.09885 16.4657 7.87154 16.5091 7.48619C16.5513 7.11216 16.6839 6.65377 16.903 6.14798C17.3409 5.13721 18.0912 4.01218 18.9913 3.11144C19.8998 2.2022 20.9035 1.57807 21.8376 1.45998Z" fill="currentColor"/>
            </svg>
          </div>

          <div class="prc-Range_Column">
            <div class="prc-Range_Field">
              <input class="prc-Range_Input" id="price-range-max" type="text" data-range-slider-external-el="max" placeholder="${currencySymbol}${formatNumberWithCommas(
      currentMaxValue,
    )}" data-range-max="${maxValue}" data-max-reached-limit="${
      this.dataset.maxPrice
    }" data-max-reached-int="${actualMaxValue}" data-price-range-el="max" value="${currentMaxInputValue}"/>

              <label class="prc-Range_Label" for="price-range-max" data-range-slider-external-el="maxLabel" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${formatNumberWithCommas(
      currentMaxValue,
    )}${suffix}">
                <span class="util-ScreenReaderOnly">
                  {{ 'sections.collection_template.to' | t }}:
                  {{ cart.currency.symbol }}${formatNumberWithCommas(
                    currentMaxValue,
                  )}</span>
              </label>
            </div>
          </div>
        </price-range>
      </div>
    `
  }
}

if (!window.customElements.get('external-klevu-price-filter')) {
  window.customElements.define(
    'external-klevu-price-filter',
    ExternalKlevuPriceFilter,
    {
      extends: 'form',
    },
  )
}
